import React from 'react';
import { Box } from '@mui/material';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { PageUseCase } from '../../corelogic/usecases/PageUseCase';
import { SEO } from './SEO';
import { PageHeader } from './PageSection';
import { BlockRenderer } from './blocks/BlockRenderer';

const pageUseCase = new PageUseCase();

/**
 * Renders a page that was assembled in the CMS.
 *
 * The header carries the h1 and matches the built-in pages exactly, so every
 * page on the site opens the same way whether it was written in code or in the
 * editor. Blocks then start at h2, which keeps the document outline correct
 * without asking an editor to think about heading levels.
 */
export const CmsPageView: React.FC<{ slug: string }> = ({ slug }) => {
  const { language } = useLanguage();
  const content = pageUseCase.getContent(slug, language);

  // Only reachable if a route outlives its content file, which the generated
  // index rules out. Rendering nothing beats throwing on a live site.
  if (!content) {
    return null;
  }

  return (
    <Box>
      <SEO
        customTitle={content.seo.title}
        customDescription={content.seo.description}
        customKeywords={content.seo.keywords}
      />

      <PageHeader
        eyebrow={content.eyebrow}
        title={content.title}
        titleImage={content.titleImage}
        subtitle={content.lead}
        icon={content.icon}
      />

      <BlockRenderer blocks={content.sections} />
    </Box>
  );
};
